import { EventData, GoogleAnalyticsData, PageviewData } from '../../../../models/eventTracking';

import { log } from './browserLogger';

// Provide these attributes to enable click event tracking on google analytics and our own database:
// data-evtrk-category (required, enum), data-evtrk-action (required, enum), data-evtrk-label (optional, free text), data-evtrk-value (optional, number)

// Or send events programmatically using sendEvent()

// Inspired by:
// * https://github.com/segmentio/analytics.js-core
// * https://github.com/googleanalytics/autotrack/blob/master/lib/plugins/event-tracker.js

const EV_TRACK_URL = '/evtrk';
const EV_TRACK_PAGEVIEW_URL = '/evtrk-pg';
const EV_TRACK_PAGE_EXIT_URL = '/evtrk-pg-exit';

function isDebugMode(): boolean {
  return window.embark && window.embark.debug;
}

export function registerPageview(): void {
  // We only record pageviews for logged-in users
  if (!window.embarkUserIdObject || !window.embarkUserIdObject.email) {
    return;
  }
  sendPageviewToEmbark(location.pathname, location.search, location.hash);
  if (!window.addEventListener) {
    log.error('event-tracking registerPageview: Missing window.addEventListener');
    return;
  }
  window.addEventListener('beforeunload', function () {
    const endTime = new Date();
    const timeOnPageSecs = (endTime.getTime() - window.embark.evtrkStartTime.getTime()) / 1000;
    sendPageExitToEmbark(location.pathname, location.search, location.hash, timeOnPageSecs);
  });
}

export function registerTracking(): void {
  if (!document.addEventListener) {
    log.error('event-tracking registerTracking: Missing document.addEventListener');
    return;
  }
  delegate('click', '[data-evtrk-event]', handleEvent);
}

export function sendPageview(pageviewData: PageviewData): void {
  log.trace({ ...pageviewData }, 'pageview called');
  sendPageviewToEmbark(pageviewData.pathname, pageviewData.search, pageviewData.hash);
}

export function sendEndPageView(pageviewData: PageviewData, timeOnPage: number): void {
  log.trace({ ...pageviewData, timeOnPage }, 'pageview exit called');
  sendPageExitToEmbark(pageviewData.pathname, pageviewData.search, pageviewData.hash, timeOnPage);
}

export function sendEvent(eventData: EventData): void {
  sendEventToEmbark(eventData);
  sendEventToGoogleAnalytics(eventData);
}

function handleEvent(event, targetElement): void {
  if (!targetElement) {
    log.error('event-tracking: Missing targetElement');
    return;
  }
  if (!targetElement.dataset.evtrkCategory) {
    log.error('event-tracking: Missing dataset.evtrkCategory');
    return;
  }

  const {
    evtrkCategory: eventCategory,
    evtrkAction: eventAction,
    evtrkLabel: eventLabel = null,
    evtrkValue: eventValue = null,
    evtrkPetNum: petNum = null,
    evtrkHandle: handle = null,
  } = targetElement.dataset;
  if (!eventCategory) {
    log.error('event-tracking: Missing eventCategory');
    return;
  }
  if (!eventAction) {
    log.error('event-tracking: Missing eventAction');
    return;
  }

  const eventData: EventData = {
    eventCategory,
    eventAction,
    eventLabel,
    eventValue: eventValue === null ? null : parseFloat(eventValue),
    petNum: petNum === null ? null : parseInt(petNum, 10),
    handle,
  };

  // Special handling for link (anchor) elements. Most link clicks cause the current page to unload because they initiate a page navigation
  if (!navigator.sendBeacon && targetElement.tagName.toLowerCase() === 'a' && getHrefForLink(targetElement)) {
    const href = getHrefForLink(targetElement);
    if (isDebugMode()) {
      log.info('Special handling for link click', href);
    }
    // Adds a new event handler at the last minute to minimize the chances
    // that another event handler for this click will run after this logic.
    const clickHandler = (): void => {
      document.removeEventListener('click', clickHandler);

      // Checks to make sure another event handler hasn't already prevented
      // the default action. If it has the custom redirect isn't needed.
      if (!event.defaultPrevented) {
        // Stops the click and waits until the hit is complete (using
        // timeout) for browsers that don't support beacon.
        event.preventDefault();

        setTimeout(function () {
          location.href = href;
        }, 800);
      }
    };
    document.addEventListener('click', clickHandler);
  }

  sendEvent(eventData);
}

function getHrefForLink(linkElement): string {
  return (
    linkElement.getAttribute('href') ||
    linkElement.getAttribute('xlink:href') ||
    linkElement.getAttributeNS('http://www.w3.org/1999/xlink', 'href')
  );
}

function sendEventToEmbark(eventData: EventData): void {
  sendToEmbark(eventData, EV_TRACK_URL);
}

function sendPageviewToEmbark(pathname: string, search: string, hash: string): void {
  sendToEmbark({ pathname, search, hash }, EV_TRACK_PAGEVIEW_URL);
}

function sendPageExitToEmbark(pathname: string, search: string, hash: string, timeOnPageSecs: number): void {
  sendToEmbark({ pathname, search, hash, timeOnPageSecs }, EV_TRACK_PAGE_EXIT_URL);
}

function sendToEmbark(data: any, url: string): void {
  const dataWithScreenWidth = {
    ...data,
    screenWidth: window.screen.width,
  };
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, JSON.stringify(dataWithScreenWidth));
    if (isDebugMode()) {
      log.info('Embark event sent using navigator.sendBeacon', dataWithScreenWidth);
    }
  } else {
    const client = new XMLHttpRequest();
    client.open('POST', url, false); // third parameter indicates sync xhr
    client.setRequestHeader('Content-Type', 'text/plain; charset=UTF-8');
    client.send(JSON.stringify(dataWithScreenWidth));
    if (isDebugMode()) {
      log.info('Embark event sent using XMLHttpRequest', dataWithScreenWidth);
    }
  }
}

function sendEventToGoogleAnalytics(eventData: EventData): void {
  if (window.ga) {
    const gaData: GoogleAnalyticsData = {
      eventCategory: eventData.eventCategory,
      eventAction: eventData.eventAction,
      eventLabel: eventData.eventLabel,
      eventValue: eventData.eventValue,
    };
    // Must use "named" google analytics tracker (name: 'embarkvet') because third party scripts like Talkable can override and break the default (unnamed) GA tracker. So we use `embarkvet.send` instead of just `send`.
    window.ga('embarkvet.send', {
      transport: 'beacon',
      hitType: 'event',
      ...gaData,
    });
    if (isDebugMode()) {
      log.info('GA event sent', eventData);
    }
  } else if (isDebugMode()) {
    log.info('GA not available', eventData);
  }
}

interface ChildNode {
  parentNode?: (Node & ParentNode) | null;
}

function delegate(eventName: string, selector: string, fn): void {
  document.addEventListener(eventName, function (event) {
    const possibleDelegateTargets = document.querySelectorAll(selector);
    const eventTarget = event.target as ChildNode;

    for (const delegateTarget of Array.from(possibleDelegateTargets)) {
      // simulated bubbling: Walk up the tree of the ancestors of eventTarget to see if we come across a delegateTarget
      let el = eventTarget;
      while (el && el !== document) {
        if (el === delegateTarget) {
          return fn.call(delegateTarget, event, delegateTarget);
        }
        el = el.parentNode as ChildNode;
      }
    }
  });
}
